import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment } from 'react';

import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import MoreIcon from '@mui/icons-material/More';

import SelectAllIcon from '@mui/icons-material/SelectAll';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';

import NumberFormat from 'react-number-format';
import TextFieldRight from 'components/ui/TextFieldRight';

import { IEntity, IResult } from 'library/interface';

import useRegistrationMarkService from './services/RegistrationMark';

import useUtils from 'library/utils';

import { IRegistrationMark, IRegistrationMarkCompetence, ISchoolYearClassSubjectMark, defaultRegistrationMark, defaultRegistrationMarkCompetence, defaultSchoolYearClassSubjectMark } from './models/RegistrationMark';

import useEntityService, { useBasicFilterEntity, useBasicFilterFeatureDescription } from 'features/services/Entity';
import {
  currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom,
  isSearchBoxShowAtom, isSaveLoadingAtom,
  colorsAtom
} from 'library/store';

import externalLibraryService from 'features/configuration/services/ExternalLibrary';
import { defaultExternalLibrary, defaultExternalLibraryClass, IExternalLibrary, IExternalLibraryClass } from 'features/configuration/models/ExternalLibrary';

import useRegistrationService from 'features/production/services/Registration';


import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { isFalsy } from 'utility-types';
import { FormDialog } from 'components/ui/FormDialog';
import { Checkbox, Chip, FormControlLabel, FormHelperText, IconButton, InputAdornment, Link, Typography } from '@mui/material';
import IEnumeration, {
  IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE, Enum_DAY_WEEK,
  Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_TEACHER_STATUS, Enum_SUBJECT,
  Enum_REGISTRATION_MARK_PURPOSE, Enum_SCHOOL_CLASS_SUBJECT_EXAM_STATUS
} from 'features/configuration/models/Enumeration';

import useEnumerationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';

import useTeacherService from 'features/setup/services/Teacher';

import { BasicTextFilterForm, defaultBasicTextFilterProps } from 'components/ui/BasicTextFilterForm';
import { defaultSchoolYearClassTemplate, defaultSchoolYearTemplateSubject, ISchoolYearClass, ISchoolYearClassSubject, ISchoolYearClassSubjectExamSchedule, ISchoolYearClassTemplate, ISchoolYearPeriod, ISchoolYearPeriodExam, ISchoolYearTemplateSubject, ISchoolYearTemplateSubjectExamSchedule, ISchoolYearTemplateSubjectExamScheduleCompetence } from 'features/setup/models/SchoolYear';
import useSchoolYearService, { useBasicFilterSchoolYearClass } from 'features/setup/services/SchoolYear';
import { DatePicker } from '@mui/x-date-pickers';
import { typographyGroupBoxStyling } from 'themes/commonStyles';

import { debounce } from 'lodash';
import EnhancedTable from 'components/ui/EnhancedTable';
import BasicImport2Form from 'components/ui/BasicImport2Form';
import { exportToExcelExMultipleSheets } from 'library/xlsx-export';
import { useDownloadTextFile } from 'library/useDownloadTextFile';
//import { useBasicFilterApplicationQuery } from 'features/setup/services/ApplicationQuery';


import { Triangle, Grid as GridSpinner } from 'react-loader-spinner';
import { BorderColor, DoneAll, DoNotDisturbOn, TaskAlt, ToggleOn, WorkspacePremium } from '@mui/icons-material';

import { ExcludeSubjectRegistrationsForm } from 'features/setup/ui/ExcludeSubjectRegistrationsForm';
import { IRegistration } from './models/Registration';

export const SchoolYearClassSubjectMarkForm: FC<ISchoolYearClassSubjectMark> = (props: ISchoolYearClassSubjectMark = defaultSchoolYearClassSubjectMark) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { id } = useParams();

  const { findDuplicate, findDuplicate2, findDuplicate3, parseDate, parseTime } = useUtils();

  const { language: lg, schoolYears, isTeacher, applicationSetup, teacherId, roleEntities } = useRecoilValue(currentUserSessionAtom);
  const [Colors, setColors] = useRecoilState(colorsAtom);

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { displayDownload: displayDownloadErrorLogs } = useDownloadTextFile();

  const { enqueueSnackbar } = useSnackbar();

  const { retrieveEntity, retrieveData, openEntityActionDrawer, checkEntitySaveAuthorization } = useEntityService();
  const { saveSchoolYearClassSubjectMark } = useRegistrationMarkService();

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();

  const { getSchoolYearClassTemplates, getSchoolYearClassRegistrationMarks, getSchoolYearClassSubjects,
    getSchoolYearClassSubjectsByPeriodExamTeacher,
    getSchoolYearPeriods, getSchoolYearPeriodExams, getSchoolYearTemplateSubjectExamSchedules,
    getSchoolYearClassSubjectExamSchedules, excludeSubjectRegistrationsEx, getSchoolYearClassesByTeacher, 
    getSchoolYearClassesByStudyLevel } = useSchoolYearService();

  const registrationServiceRef = useRef(useRegistrationService());

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);

  const refSchoolYearClassTemplate = useRef<ISchoolYearClassTemplate>(defaultSchoolYearClassTemplate);
  // const basicFilterApplicationQuery = useBasicFilterApplicationQuery( 

  //   async (event: React.MouseEvent<unknown>, row: IApplicationQuery) => {
  //     const {id, name } = row;

  //     setIsSearchBoxShow(false);
  //     _setId(row.id);
  //   }
  // );

  const [openClassFilter, setOpenClassFilter] = useState(false);
  const basicFilterSchoolYearClass = useBasicFilterSchoolYearClass(
    async (event: React.MouseEvent<unknown>, row: ISchoolYearClass) => {

      //const schoolYearClass = await retrieveEntity('SchoolYearClass',row.id) as ISchoolYearClass;

      onSchoolYearClassSelected(row);
      setOpenClassFilter(false);
    }
  );

  const onSchoolYearClassSelected = async (schoolYearClass: ISchoolYearClass) => {
    const { id, studyType, name, schoolYearClassTemplateId, studyLevel, 
      classTemplateName, studyLanguage, fullTeacherName, substituteTeacherName, schoolYearId } = schoolYearClass;

    const data = getValues();
    reset({
      ...data, schoolYearClassId: id,
      schoolYearId, schoolYearClassTemplateId,

      schoolYearClassName: name, studyType, studyLevel,
      fullTeacherName, substituteTeacherName, classTemplateName, studyLanguage,
      registrationMarks: []
    });

    setValue('schoolYearId', schoolYearId);
    const arr = await getSchoolYearClassSubjects(id); // schoolYearClassId
    setAllSchoolYearClassSubjects(arr);

    if (isTeacher) {
      setSchoolYearClassSubjects([]);
    } else {
      setSchoolYearClassSubjects([...arr]);
    }

    const arr2 = await getSchoolYearClassSubjectExamSchedules(id, 0, 0);
    setSchoolYearClassSubjectExamSchedules(arr2);
  }


  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;


    }
  );



  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity(
    (event: React.MouseEvent<unknown>, row: IEntity) => {
      const { name, description } = row;


    }
  );


  const emptyFunc = (obj: any) => { }

  const methods = useForm<ISchoolYearClassSubjectMark>({ defaultValues: defaultSchoolYearClassSubjectMark });
  const { register, setValue, getValues, watch, reset, handleSubmit, setFocus, control, formState: { errors } } = methods;

  const watchSchoolYearId = watch('schoolYearId');
  const watchSchoolYearClassId = watch('schoolYearClassId');
  const watchSchoolYearPeriodId = watch('schoolYearPeriodId');
  const watchSchoolYearPeriodExamId = watch('schoolYearPeriodExamId');
  const watchSchoolYearClassSubjectId = watch('schoolYearClassSubjectId');

  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');
  const watchCheckMarkByCompetence = watch('checkMarkByCompetence');

  const watchStudyLevel = watch('studyLevel');

  const watchAutoDistributeMarkByCompetence = watch('autoDistributeMarkByCompetence');

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<ISchoolYearClassSubjectMark>, Error, ISchoolYearClassSubjectMark>(
    saveSchoolYearClassSubjectMark, {
    onSuccess: async (data: IResult<ISchoolYearClassSubjectMark>) => {
      enqueueSnackbar(t('Operation done !!!'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      // const schoolYearClassSubjectExamSchedule = schoolYearClassSubjectExamSchedules.find(
      //     x => x.schoolYearClassId === data.data.schoolYearClassId &&
      //     x.schoolYearTemplateSubjectExamScheduleId === data.data.schoolYearTemplateSubjectExamScheduleId );

      // setSchoolYearClassSubjectExamSchedules(schoolYearClassSubjectExamSchedules.map( x => {

      //   return {...x,
      //       id: isFalsy(schoolYearClassSubjectExamSchedule)? x.id : schoolYearClassSubjectExamSchedule.id,
      //       status: isFalsy(schoolYearClassSubjectExamSchedule)? x.status : schoolYearClassSubjectExamSchedule.status
      //   }
      // }));
      const arr = await getSchoolYearClassSubjectExamSchedules(data.data.schoolYearClassId, 0, 0);
      setSchoolYearClassSubjectExamSchedules(arr);

      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);

      queryClient.invalidateQueries(['SchoolYearClassSubjectMark', data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });


  const { mutate: mutateExcludeSubjectRegistrations } = useMutation<IResult<boolean>, Error, any>(
    excludeSubjectRegistrationsEx, {
    onSuccess: (data: IResult<boolean>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setOpenExcludeSubjectRegistrationsDialog(false);

      //queryClient.invalidateQueries(['Registration',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setOpenExcludeSubjectRegistrationsDialog(false);
    }
  });

  const { data: _data, refetch } = useQuery<ISchoolYearClassSubjectMark>(['SchoolYearClassSubjectMark', _id], () => retrieveEntity('SchoolYearClassSubjectMark', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: schoolYearClassTemplate, refetch: refetchSchoolYearClassTemplate } = useQuery<ISchoolYearClassTemplate>(['SchoolYearClassSubjectMark', 'SchoolYearClassTemplate', watchSchoolYearClassTemplateId],
    () => retrieveEntity('SchoolYearClassTemplate', watchSchoolYearClassTemplateId), { refetchOnWindowFocus: false, enabled: watchSchoolYearClassTemplateId > 0 });

  const { data: enumItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'SchoolYearClassTemplate'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_REGISTRATION_STATUS, Enum_TEACHER_STATUS, Enum_DAY_WEEK,
      Enum_STUDY_TYPE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_SUBJECT,
      Enum_REGISTRATION_MARK_PURPOSE, Enum_SCHOOL_CLASS_SUBJECT_EXAM_STATUS]));

  const [schoolYearPeriods, setSchoolYearPeriods] = useState<ISchoolYearPeriod[]>([]);
  const [schoolYearPeriodExams, setSchoolYearPeriodExams] = useState<ISchoolYearPeriodExam[]>([]);

  const [schoolYearClassSubjects, setSchoolYearClassSubjects] = useState<ISchoolYearClassSubject[]>([]);
  const [allSchoolYearClassSubjects, setAllSchoolYearClassSubjects] = useState<ISchoolYearClassSubject[]>([]);

  const [schoolYearClassSubjectExamSchedules, setSchoolYearClassSubjectExamSchedules] = useState<ISchoolYearClassSubjectExamSchedule[]>([]);

  //const [schoolYearTemplateSubjectExamSchedules, setSchoolYearTemplateSubjectExamSchedules] = useState<ISchoolYearTemplateSubjectExamSchedule[]>([]);

  // const [currentLibrary, setCurrentLibrary] = useState<IExternalLibrary>(defaultExternalLibrary);
  // const [currentLibraryClass, setCurrentLibraryClass] = useState<IExternalLibraryClass>(defaultExternalLibraryClass);

  // const [dbmsType, setDbmsType] = useState<string>('');

  // const [applicationQueryIndex, setApplicationQueryIndex] = useState<number>(-1);
  // const [applicationQueryParameterIndex, setRegistrationMarkIndex] = useState<number>(-1);

  // const [openAppQueryParamDialog, setOpenAppQueryParamDialog] = useState<boolean>(false);

  const handleClassNavigationClick = (event: any) => {
    if (!roleEntities.some(re => re.entityName === 'SchoolYearClass')) return;

    navigate(`/SchoolYearClass/${getValues().schoolYearClassId}`);
  }

  

  const inputLogin = useRef();
  const cellKeyDownMark = (e: React.KeyboardEvent<HTMLInputElement>, row: IRegistrationMark, index: number) => {

    if (!['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key))
      return;

    const cellId = 'mark';
    const rowsPathNameDef = 'registrationMarks';
    const len = getValues().registrationMarks.length;

    // let idx = ['Enter', 'ArrowDown'].includes(e.key) ?
    //   Math.min(index + 1, len - 1) : Math.max(index - 1, 0);

    let idx = index;

    do {
      idx++;
    } while (idx < len && !getValues().registrationMarks[idx].markExist)

    const elt = document.getElementById(`${rowsPathNameDef}-id-${cellId}-${idx}`); // This is how id is set in ArrayFeldTableEx
    if (isFalsy(elt))
      return;

    elt!.focus();
    (elt! as HTMLInputElement).select();
  }

  const cellEditableRegistrationMark = (row: IRegistrationMark, cellId: keyof IRegistrationMark) => {
    //row.markExist

    return (!row.isSubjectExcluded || row.markExist)
      && ['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)
      && watchSchoolYearPeriodId > 0 && watchSchoolYearPeriodExamId > 0 && watchSchoolYearClassSubjectId > 0;
  }

  const cellAllowedMark = (row: IRegistrationMark, valueMark: number) => {
    if (valueMark > getValues().maxMark)
      enqueueSnackbar(`${t('Invalid mark, the mark must be less than the max allowed')} : ${getValues().maxMark} !!!`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });

    const allowed = valueMark >= 0 && valueMark <= getValues().maxMark;
    debouncedMark();

    return allowed;
  }

  const textDecorationExcluded = (row: IRegistrationMark, index: number): string => {

    return row.markExist ? '' : 'line-through';
  }

  const textDecorationMark = (row: IRegistrationMark, index: number): string => {

    if (!row.markExist) return '';

    if (isFalsy(refSchoolYearClassTemplate.current)) { console.log({ schoolYearClassTemplate, msg: 'Here 1' }); return 'line-through'; }

    const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) { console.log({ schoolYearClassSubject, msg: 'Here 2' }); return 'line-through'; }

    const schoolYearTemplateSubject = refSchoolYearClassTemplate.current.schoolYearTemplateSubjects.find(
      s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
    if (isFalsy(schoolYearTemplateSubject)) { console.log({ schoolYearTemplateSubject, msg: 'Here 3' }); return 'line-through'; }

    const schoolYearTemplateSubjectExamSchedule = schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find(
      e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

    if (isFalsy(schoolYearTemplateSubjectExamSchedule)) { console.log({ schoolYearTemplateSubjectExamSchedule, msg: 'Here 4' }); return 'line-through'; }

    if (schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !==
      row.registrationMarkCompetences.length) { console.log('Here line-through'); return 'line-through'; }

    if (schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length === 0)
      return '';

    const { maxMark } = getValues();

    if (row.registrationMarkCompetences.some(x => x.maxMark <= 0)) { console.log('Here line-through'); return 'line-through'; }

    if (row.registrationMarkCompetences.some(x => x.maxMark < x.mark)) { console.log('Here 8'); return 'line-through double'; }

    const sumMark = row.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.mark, 0);
    //const sumMaxMark = row.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark, 0);

    const markCompetence = sumMark; //(sumMark * maxMark) / sumMaxMark;

    if (Math.abs(markCompetence - row.mark) > 0.0001) { console.log({ row, markCompetence, msg: 'Here line-through' }); return 'line-through'; }
    //const registrationMarkCompetences = row.registrationMarkCompetences;
    // console.log('Here 11');
    return '';
  }




  useEffect(() => {

    refSchoolYearClassTemplate.current = schoolYearClassTemplate || defaultSchoolYearClassTemplate;
  }, [schoolYearClassTemplate]);

  useEffect(() => {

    const subscription = watch(async (value, { name, type }) => {

      if (name?.startsWith('registrationMarks') && name?.endsWith('markExist')) {

        debouncedMark();
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const debouncedMark = useRef(
    debounce(() => {
      //const sumAmount = sum( getValues().tellerOperationBillings.map( ({amount}) => amount ) );
      //setValue('amount', sumAmount);

      const { maxMark, autoDistributeMarkByCompetence } = getValues();

      const marks = getValues().registrationMarks.map(x => x.mark).sort((a, b) => b - a); // desc.
      const markSet = new Set(marks);
      const len = getValues().registrationMarks.length;

      markSet.forEach(function (mark) {

        for (var i = 0; i < len; i++) {
          const registrationMark = getValues().registrationMarks[i];

          if (registrationMark.mark !== mark) continue;

          const calcRank = getValues().registrationMarks.filter(x => x.markExist && x.mark > mark).length;

          (refUpdateRegistrationMark.current ?? emptyFunc)(i, {
            ...registrationMark,
            mark: registrationMark.markExist ? mark : 0,
            rank: registrationMark.markExist ? calcRank + 1 : 0,

            registrationMarkCompetences: registrationMark.registrationMarkCompetences.map(c => {

              const sumMax = registrationMark.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark, 0);

              return {
                ...c,
                registrationMarkId: registrationMark.id,
                markExist: registrationMark.markExist ? c.markExist : false,
                mark: (registrationMark.markExist && c.markExist && sumMax !== 0) ?
                  (autoDistributeMarkByCompetence ? mark * c.maxMark / sumMax : c.mark) : 0

              }
            })
          });
        }

      });


    }, 10000)
  ).current;


  const [headRegistrationMarkCells, setHeadRegistrationMarkCells] = useState<HeadCell<IRegistrationMark>[]>([]);
  useEffect(() => {
    setHeadRegistrationMarkCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },

      { id: 'status', label: t('Status'), display: true, type: 'string', width: 10, },

      {
        id: lg.includes('en') ? 'firstName' : 'lastName', label: lg.includes('en') ? t('First name') : t('Last name'),
        display: true, type: 'string', width: 25, textDecoration: textDecorationExcluded,
      },
      {
        id: lg.includes('en') ? 'lastName' : 'firstName', label: lg.includes('en') ? t('Last name') : t('First name'),
        display: true, type: 'string', width: 25, textDecoration: textDecorationExcluded,
      },

      { id: 'currentMark', label: t('Cur.'), display: true, type: 'numeric', width: 10, decimalScale: 2 },
      {
        id: 'mark', label: t('Mark'), display: true, type: 'numeric', width: 10, decimalScale: 2,
        isEditable: cellEditableRegistrationMark, isAllowed: cellAllowedMark, textDecoration: textDecorationMark,
        handleKeyDown: cellKeyDownMark,
      },
      {
        id: 'markExist', label: t('Exist ?'), display: true, type: 'boolean', width: 10,
        isEditable: cellEditableRegistrationMark
      },
      { id: 'rank', label: t('Rank'), display: true, type: 'numeric', width: 10 },
    ])
  }, [t, i18n, watchSchoolYearPeriodId, watchSchoolYearPeriodExamId, watchSchoolYearClassSubjectId])

  const refAppendRegistrationMarks = useRef<(value: Partial<FieldArray<ISchoolYearClassSubjectMark>> | Partial<FieldArray<ISchoolYearClassSubjectMark>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateRegistrationMark = useRef<(index: number, value: Partial<FieldArray<ISchoolYearClassSubjectMark>>) => void>(null);
  const refRemoveRegistrationMark = useRef<(index: number) => void>(null);

  const [defineAllMark, setDefineAllMark] = useState<boolean>(false);
  const [selectedRegistrationsIndex, setSelectedRegistrationsIndex] = useState<number[]>([]);
  const [openSameMarkDialog, setOpenSameMarkDialog] = useState<boolean>(false);
  const [sameMark, setSameMark] = useState<number>(0);
  const handleSetMarkForSelected = (event: any) => {
    if (isFalsy(getValues().registrationMarks.length)) return;
    if (isFalsy(selectedRegistrationsIndex.length)) return;
    if (!['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)) return;

    setDefineAllMark(false);
    setOpenSameMarkDialog(true);
  }

  const handleSetMarkForAll = (event: any) => {
    if (isFalsy(getValues().registrationMarks.length)) return;
    if (!['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)) return;

    setDefineAllMark(true);
    setOpenSameMarkDialog(true);
  }

  const handleSetMarkOk = (event: any) => {
    if (sameMark < 0 || sameMark > getValues().maxMark) {
      enqueueSnackbar(`${t('Invalid mark, the mark must be less than the max allowed')} : ${getValues().maxMark} !!!`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (defineAllMark) {
      for (var k = 0; k < getValues().registrationMarks.length; k++) {
        const registrationMark = getValues().registrationMarks[k];
        const { markExist } = registrationMark;
        (refUpdateRegistrationMark.current ?? emptyFunc)(k, { ...registrationMark, mark: markExist ? sameMark : 0 });
      }
    } else {
      for (var i = 0; i < selectedRegistrationsIndex.length; i++) {
        const k = selectedRegistrationsIndex[i];
        if (k < 0 || k >= getValues().registrationMarks.length) continue;

        const registrationMark = getValues().registrationMarks[k];
        const { markExist } = registrationMark;
        (refUpdateRegistrationMark.current ?? emptyFunc)(k, { ...registrationMark, mark: markExist ? sameMark : 0 });
      }
    }

    debouncedMark();
    setOpenSameMarkDialog(false);
  }

  const [openExcludeIncludeDialog, setOpenExcludeIncludeDialog] = useState<boolean>(false);
  const [sameExcludeInclude, setSameExcludeInclude] = useState<boolean>(true);

  const [sameExcludeIncludePurpose, setSameExcludeIncludePurpose] = useState<string>('');
  const handleExcludeOrIncludeMarkForAll = (event: any) => {
    setOpenExcludeIncludeDialog(true);
  }

  const handleExcludeOrIncludeMarkForAllOk = (event: any) => {
    const { registrationMarks } = getValues();

    setValue('registrationMarks', registrationMarks.map(x => ({
      ...x,
      markExist: sameExcludeInclude, mark: sameExcludeInclude ? x.mark : 0, markPurpose: sameExcludeInclude ? sameExcludeIncludePurpose : ''
    })));

    setOpenExcludeIncludeDialog(false);
    debouncedMark();
  }

  const [openSameCompetenceMarkDialog, setOpenSameCompetenceMarkDialog] = useState<boolean>(false);
  const [selectedCompetenceId, setSelectedCompetenceId] = useState<number>(0);
  const [sameCompetenceMark, setSameCompetenceMark] = useState<number>(0);
  const handleSetCompetenceMarkForAll = (event: any) => {

    setOpenSameCompetenceMarkDialog(true);
  }

  const handleSetCompetenceMarkForAllOk = (event: any) => {

    const selectedCompetence = getCurrentExamCompetences().find(x => x.id === selectedCompetenceId);
    const { registrationMarks } = getValues();

    if (isFalsy(selectedCompetence)) return;

    setValue('registrationMarks', registrationMarks.map(x => ({
      ...x,
      registrationMarkCompetences: x.registrationMarkCompetences.map(y => ({
        ...y,
        mark: !y.markExist ? 0 :
          y.schoolYearTemplateSubjectCompetenceId === selectedCompetence.schoolYearTemplateSubjectCompetenceId
            ? sameCompetenceMark : y.mark
      }))
    })));

    setOpenSameCompetenceMarkDialog(false);
    debouncedMark();
  }

  const [openExcludeIncludeCompetenceDialog, setOpenExcludeIncludeCompetenceDialog] = useState<boolean>(false);
  const [sameExcludeIncludeCompetence, setSameExcludeIncludeCompetence] = useState<boolean>(true);
  const handleExcludeOrIncludeCompetenceMarkForAll = (event: any) => {
    setOpenExcludeIncludeCompetenceDialog(true);
  }

  const handleExcludeOrIncludeCompetenceMarkForAllOk = (event: any) => {

    const selectedCompetence = getCurrentExamCompetences().find(x => x.schoolYearTemplateSubjectCompetenceId === selectedCompetenceId);
    const { registrationMarks } = getValues();

    if (isFalsy(selectedCompetence)) return;

    setValue('registrationMarks', registrationMarks.map(x => ({
      ...x,
      registrationMarkCompetences: x.registrationMarkCompetences.map(y => ({
        ...y,
        markExist: y.schoolYearTemplateSubjectCompetenceId === selectedCompetence.schoolYearTemplateSubjectCompetenceId ?
          sameExcludeIncludeCompetence : y.markExist,
        mark: y.schoolYearTemplateSubjectCompetenceId === selectedCompetence.schoolYearTemplateSubjectCompetenceId
          && !sameExcludeIncludeCompetence ? 0 : y.mark
      }))
    })));

    setOpenExcludeIncludeCompetenceDialog(false);
    debouncedMark();
  }

  const handleDownloadMarks = (event: any) => {
    if (isFalsy(getValues().registrationMarks.length)) return;

    exportToExcelExMultipleSheets(
      [
        {
          items: getValues().registrationMarks, sheetName: `${t('Mark')}`,
          columns2Export: ['firstName', 'lastName', 'mark']
        },
        {
          items: getValues().registrationMarks.flatMap(({ registrationMarkCompetences }) => registrationMarkCompetences),
          sheetName: `${t('Mark')}-${t('Competences')}`,
          columns2Export: ['firstName', 'lastName', 'schoolYearTemplateSubjectCompetenceTitle', 'mark']
        }
      ],
      `${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}`
    );
  }

  const [markObjecBasicImport2, setMarkObjecBasicImport2] = useState<
    { first: IRegistrationMark[], second: IRegistrationMarkCompetence[] }>({ first: [], second: [] });

  const [openBasicImport2Form, setOpenBasicImport2Form] = useState<boolean>(false);
  const handleLoadMarks = (event: any) => {
    if (isFalsy(getValues().registrationMarks.length)) return;
    if (!['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)) return;

    setOpenBasicImport2Form(true);
  }

  const handleOKBasicImport2Marks = (event: any) => {

    if (isFalsy(getValues().registrationMarks.length)) return;
    if (!['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)) return;

    if (isFalsy(schoolYearClassTemplate)) return;

    const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) return;

    const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
      s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
    if (isFalsy(schoolYearTemplateSubject)) return;

    const schoolYearTemplateSubjectExamSchedule = schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find(
      e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

    if (isFalsy(schoolYearTemplateSubjectExamSchedule)) return;

    const sheetRegistrationMarks = markObjecBasicImport2.first;
    const sheetRegistrationMarkCompetences = markObjecBasicImport2.second;

    const duplicateRegistrationMark = findDuplicate2(sheetRegistrationMarks, 'firstName', 'lastName');
    if (!isFalsy(duplicateRegistrationMark)) {
      enqueueSnackbar(`${duplicateRegistrationMark.firstName} ${duplicateRegistrationMark.lastName} ${t('is more than one, check that and others.')}`,
        { variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500 });
      return;
    }

    const duplicateRegistrationMarkCompetence = findDuplicate3(sheetRegistrationMarkCompetences, 'firstName', 'lastName', 'schoolYearTemplateSubjectCompetenceTitle');
    if (!isFalsy(duplicateRegistrationMarkCompetence)) {
      enqueueSnackbar(`${duplicateRegistrationMarkCompetence.firstName} ${duplicateRegistrationMarkCompetence.lastName} 
          ${duplicateRegistrationMarkCompetence.schoolYearTemplateSubjectCompetenceTitle} ${t('is more than one, check that and others.')}`,
        { variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500 });
      return;
    }


    const { registrationMarks } = getValues();

    const errorLogs = [] as string[];

    for (const regMark of sheetRegistrationMarks.filter(r => !isFalsy(r.firstName) || !isFalsy(r.lastName))) {


      const { firstName, lastName, mark } = regMark;
      const indexRegistrationMark = registrationMarks.findIndex(r =>
        r.firstName.trim() === firstName.trim() && r.lastName.trim() === lastName.trim());

      if (indexRegistrationMark >= 0) {
        const registrationMark = registrationMarks[indexRegistrationMark];

        setValue(`registrationMarks.${indexRegistrationMark}.mark`, mark);

        const registrationMarkCompetences = schoolYearTemplateSubjectExamSchedule?.schoolYearTemplateSubjectExamScheduleCompetences.map(
          (c) => {

            const markCompetence = registrationMark.registrationMarkCompetences.find(x => x.schoolYearTemplateSubjectExamScheduleCompetenceId === c.id);

            return {
              ...c,
              id: isFalsy(markCompetence) ? 0 : markCompetence!.id,

              registrationMarkId: registrationMark.id, status: 'N',
              currentMark: isFalsy(markCompetence) ? 0 : markCompetence!.currentMark, maxMark: c.maxMark,
              mark: isFalsy(markCompetence) ? 0 : markCompetence!.mark,
              rank: isFalsy(markCompetence) ? 0 : markCompetence!.rank,
              markExist: isFalsy(markCompetence) ? true : markCompetence!.markExist,
              markPurpose: isFalsy(markCompetence) ? '' : markCompetence!.markPurpose,
              markAppreciationId: isFalsy(markCompetence) ? -1 : markCompetence!.markAppreciationId,

              schoolYearTemplateSubjectExamScheduleCompetenceId: c.id,
              schoolYearTemplateSubjectCompetenceDescription: c.schoolYearTemplateSubjectCompetenceDescription,

              firstName: registrationMark.firstName, lastName: registrationMark.lastName,
              schoolYearTemplateSubjectCompetenceTitle: c.title
            };
          }) as unknown as IRegistrationMarkCompetence[];

        setValue(`registrationMarks.${indexRegistrationMark}.registrationMarkCompetences`, registrationMarkCompetences);
      } else
        errorLogs.push(`${t('Marks')} :  ${t('Invalid data !!!')} ${firstName} ${lastName}`);
    }

    for (const competence of
      sheetRegistrationMarkCompetences.filter(c => (!isFalsy(c.firstName) || !isFalsy(c.lastName))
        && !isFalsy(c.schoolYearTemplateSubjectCompetenceTitle))) {

      const { firstName, lastName, schoolYearTemplateSubjectCompetenceTitle, mark } = competence;

      const indexRegistrationMark = registrationMarks.findIndex(r =>
        r.firstName.trim() === firstName.trim() && r.lastName.trim() === lastName.trim());

      if (indexRegistrationMark >= 0) {
        const registrationMark = registrationMarks[indexRegistrationMark];

        const indexCompetence = registrationMark.registrationMarkCompetences.findIndex(c =>
          c.schoolYearTemplateSubjectCompetenceTitle.trim() === schoolYearTemplateSubjectCompetenceTitle.trim());
        if (indexCompetence >= 0) {
          setValue(`registrationMarks.${indexRegistrationMark}.` +
            `registrationMarkCompetences.${indexCompetence}.mark` as any, mark);
        } else
          errorLogs.push(`${t('Exam')}-${t('Competence')} :  ${t('Invalid data !!!')} ${schoolYearTemplateSubjectCompetenceTitle}`);
      } else
        errorLogs.push(`${t('Mark')}-${t('Competence')} :  ${t('Invalid data !!!')} ${firstName} ${lastName}`);

    }

    if (errorLogs.length > 0)
      displayDownloadErrorLogs(t('Importation file contains errors'), 'error', `${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}.txt`, errorLogs)

    setOpenBasicImport2Form(false);
  }

  const registrationMarkRowActionIcon = (registrationMark: IRegistrationMark): ActionIconTableRow<ISchoolYearClassSubjectMark, IRegistrationMark> => {

    const res: ActionIconTableRow<ISchoolYearClassSubjectMark, IRegistrationMark> = {
      toolTip: 'more',
      icon: MoreVertIcon,
      hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IRegistrationMark) => {

        const { maxMark } = getValues();
        const { mark } = row;

        if (isFalsy(schoolYearClassTemplate)) return;

        const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
        if (isFalsy(schoolYearClassSubject)) return;

        const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
          s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
        if (isFalsy(schoolYearTemplateSubject)) return;

        const schoolYearTemplateSubjectExamSchedule = schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find(
          e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

        const regMark = getValues().registrationMarks.at(index);
        if (isFalsy(regMark)) return;

        // const registrationMarkCompetences = schoolYearTemplateSubjectExamSchedule?.schoolYearTemplateSubjectExamScheduleCompetences.map(
        //   (c) => {
        //     const markCompetence = regMark.registrationMarkCompetences.find(x => x.schoolYearTemplateSubjectExamScheduleCompetenceId === c.id);

        //     return {
        //       ...c,
        //       id: isFalsy(markCompetence) ? 0 : markCompetence!.id,

        //       registrationMarkId: regMark.id, status: 'N',
        //       currentMark: isFalsy(markCompetence) ? 0 : markCompetence!.currentMark, maxMark: c.maxMark,
        //       mark: isFalsy(markCompetence) ? 0 : (watchAutoDistributeMarkByCompetence ? mark*c.maxMark/maxMark : markCompetence!.mark),
        //       rank: isFalsy(markCompetence) ? 0 : markCompetence!.rank,
        //       markExist: isFalsy(markCompetence) ? true : markCompetence!.markExist,
        //       markPurpose: isFalsy(markCompetence) ? '' : markCompetence!.markPurpose,
        //       markAppreciationId: isFalsy(markCompetence) ? -1 : markCompetence!.markAppreciationId,

        //       schoolYearTemplateSubjectExamScheduleCompetenceId: c.id,
        //       schoolYearTemplateSubjectCompetenceDescription: c.schoolYearTemplateSubjectCompetenceDescription,

        //       firstName: regMark.firstName, lastName: regMark.lastName,
        //       schoolYearTemplateSubjectCompetenceTitle: c.title
        //     };
        //   }) as unknown as IRegistrationMarkCompetence[];

        // setValue(`registrationMarks.${index}.registrationMarkCompetences`, registrationMarkCompetences);

        setMarkIndex(index);
        setOpenMarkPurpose(true);
      }
    }
    return res;
  }


  const cellKeyDownMarkCompetence = (e: React.KeyboardEvent<HTMLInputElement>, row: IRegistrationMarkCompetence, index: number) => {

    if (!['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;

    const cellId = 'mark';

    const curMarkIndex = getValues().registrationMarks.findIndex(x => x.id === row.registrationMarkId);
    if (curMarkIndex < 0) return;

    const rowsPathNameDef = `registrationMarks.${curMarkIndex}.registrationMarkCompetences`; /// to do: look it after ....
    const len = getValues().registrationMarks.length;

    const idx = ['Enter', 'ArrowDown'].includes(e.key) ?
      Math.min(index + 1, len - 1) : Math.max(index - 1, 0);

    const elt = document.getElementById(`${rowsPathNameDef}-id-${cellId}-${idx}`); // This is how id is set in ArrayFeldTableEx
    //console.log({ elt, id: `${rowsPathNameDef}-id-${cellId}-${idx}` })
    if (isFalsy(elt))
      return;

    elt!.focus();
    (elt! as HTMLInputElement).select();
  }

  const cellEditableRegistrationMarkCompetence = (row: IRegistrationMarkCompetence, cellId: keyof IRegistrationMarkCompetence) => {

    const registrationMark = getValues().registrationMarks.find(x => x.id === row.registrationMarkId);

    if (isFalsy(registrationMark)) return false;

    if (cellId === 'markExist' && registrationMark.markExist) return true;

    return row.markExist
      && ['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus);
  }

  const cellAllowedMarkCompetence = (row: IRegistrationMarkCompetence, valueMark: number) => {

    if (valueMark > row.maxMark)
      enqueueSnackbar(`${t('Invalid mark, the mark must be less than the max allowed')} : ${row.maxMark} !!!`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });

    const allowed = valueMark >= 0 && valueMark <= row.maxMark;
    //debouncedMark();

    return allowed;
  }

  const [headRegistrationMarkCompetenceCells, setHeadRegistrationMarkCompetenceCells] = useState<HeadCell<IRegistrationMarkCompetence>[]>([]);
  useEffect(() => {
    setHeadRegistrationMarkCompetenceCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },

      { id: 'schoolYearTemplateSubjectCompetenceDescription', label: t('Competence'), display: true, type: 'string', width: 40, },
      { id: 'status', label: t('Status'), display: true, type: 'string', width: 7, },

      { id: 'currentMark', label: t('Cur.'), display: true, type: 'numeric', width: 7, decimalScale: 2 },
      {
        id: 'mark', label: t('Mark'), display: true, type: 'numeric', width: 7, decimalScale: 2,
        isEditable: cellEditableRegistrationMarkCompetence, handleKeyDown: cellKeyDownMarkCompetence,
        isAllowed: cellAllowedMarkCompetence
      },
      {
        id: 'markExist', label: t('Exist ?'), display: true, type: 'boolean', width: 12,
        isEditable: cellEditableRegistrationMarkCompetence
      },
      { id: 'rank', label: t('Rank'), display: true, type: 'numeric', width: 7 },

      { id: 'markPurpose', label: t('Purpose'), display: true, type: 'numeric', width: 20 },
    ])
  }, [t, i18n])

  const refAppendRegistrationMarkCompetences = useRef<(value: Partial<FieldArray<ISchoolYearClassSubjectMark>> | Partial<FieldArray<ISchoolYearClassSubjectMark>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateRegistrationMarkCompetence = useRef<(index: number, value: Partial<FieldArray<ISchoolYearClassSubjectMark>>) => void>(null);
  const refRemoveRegistrationMarkCompetence = useRef<(index: number) => void>(null);

  const [selectedSchoolYearByTeacher, setSelectedSchoolYearByTeacher] = useState<number>(applicationSetup.currentSchoolYearId);
  const [schoolYearClassesByTeacher, setSchoolYearClassesByTeacher] = useState<ISchoolYearClass[]>([]);
  const [openSchoolYearClassByTeacher, setOpenSchoolYearClassByTeacher] = useState<boolean>(false);
  const handleClickOpenSchoolYearClass = async (event: any) => {
    if (isTeacher) {
      if (schoolYearClassesByTeacher.length === 0)
        setSchoolYearClassesByTeacher(await getSchoolYearClassesByTeacher(selectedSchoolYearByTeacher, teacherId));

      setOpenSchoolYearClassByTeacher(true);
    }
    else
      setOpenClassFilter(true);
  }

  const handleDoubleClickSchoolYearClassByTeacher = (event: any, row: ISchoolYearClass) => {

    onSchoolYearClassSelected(row);
    setOpenSchoolYearClassByTeacher(false);
  }

 
  const [schoolYearClassesByLevel, setSchoolYearClassesByLevel] = useState<ISchoolYearClass[]>([]);
  const [openSchoolYearClassByLevel, setOpenSchoolYearClassByLevel] = useState<boolean>(false);

  const handleFilterSameLevelClasses = async (event: any) => {
    
    setSchoolYearClassesByLevel(await getSchoolYearClassesByStudyLevel(watchStudyLevel));
    setOpenSchoolYearClassByLevel(true);
  }

 const handleDoubleClickSchoolYearClassByLevel = (event: any, row: ISchoolYearClass) => {

    onSchoolYearClassSelected(row);
    setOpenSchoolYearClassByLevel(false);
  } 


  const getSubjectExamStatusList = (row: ISchoolYearClassSubjectExamSchedule, cellId: keyof ISchoolYearClassSubjectExamSchedule,
    opts: { value: string, name: string }[]) => {

    return getAsOptions(refEnumItems.current ?? [], Enum_SCHOOL_CLASS_SUBJECT_EXAM_STATUS);
  }

  const [openSchoolYearClassSubjectExamSchedule, setOpenSchoolYearClassSubjectExamSchedule] = useState<boolean>(false);
  const handleOpenSchoolYearClassSubjectExamSchedule = async (event: any) => {

    const arr = await getSchoolYearClassSubjectExamSchedules(watchSchoolYearClassId, watchSchoolYearPeriodId, watchSchoolYearPeriodExamId);

    setSchoolYearClassSubjectExamSchedules(arr);
    setOpenSchoolYearClassSubjectExamSchedule(true);
  };

  const [markIndex, setMarkIndex] = useState<number>(-1);
  const [openMarkPurpose, setOpenMarkPurpose] = useState(false);
  const handleClickOpenMarkPurpose = (event: any) => {
    setOpenMarkPurpose(true);
  }


  const handleViewSubjectCompetences = (event: any) => {
    //console.log(schoolYearClassTemplate);
  }

  const handleViewSchedulesCompetences = (event: any) => {
  }

  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;
  }, [enumItems]);


  useEffect(() => {
    async function _() {

      if (watchSchoolYearId <= 0) return;

      const schoolYear = schoolYears.find(x => x.id === watchSchoolYearId);
      if (isFalsy(schoolYear)) return;

      setValue('schoolYearStartDate', schoolYear.startDate);
      setValue('schoolYearEndDate', schoolYear.endDate);

      const periods = await getSchoolYearPeriods(watchSchoolYearId);
      setSchoolYearPeriods([...periods]);

      const exams = await getSchoolYearPeriodExams(watchSchoolYearId);
      setSchoolYearPeriodExams([...exams]);
    }

    _();

  }, [watchSchoolYearId]);


  useEffect(() => {
    if (watchSchoolYearClassSubjectId <= 0) return;

    const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) return;

    setValue('teacherName', schoolYearClassSubject.teacherName);
    setValue('schoolYearTemplateSubjectLongName', schoolYearClassSubject.schoolYearTemplateSubjectLongName);
  }, [watchSchoolYearClassSubjectId]);


  const refSchoolYearPeriodExamName = useRef<string>('');
  useEffect(() => {
    async function _() {

      const data = getValues();

      setValue('schoolYearClassSubjectId', 0);

      if (data.schoolYearClassId <= 0 || data.schoolYearClassTemplateId <= 0) {
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      if (watchSchoolYearPeriodExamId <= 0) {
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      const schoolYearPeriodExam = schoolYearPeriodExams.find(e => e.id === watchSchoolYearPeriodExamId);
      if (isFalsy(schoolYearPeriodExam)) {
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      refSchoolYearPeriodExamName.current = schoolYearPeriodExam.name;


      // we load subject in case of teacher when exam change.
      if (isTeacher) {
        const arr = await getSchoolYearClassSubjectsByPeriodExamTeacher(watchSchoolYearClassId, watchSchoolYearPeriodExamId, teacherId); // schoolYearClassId
        setSchoolYearClassSubjects([...arr]);
      }

      // const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);
      // if(isFalsy(schoolYearClassSubject)) { 
      //   enqueueSnackbar( t('This subject is not configured in the class'), { variant: 'warning',
      //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
      //   reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00',  registrationMarks: []});
      //   return;
      // } 

      // const schoolYearClassSubjectExamSchedule = schoolYearClassSubjectExamSchedules.find(
      //   x => x.schoolYearTemplateSubjectId === schoolYearClassSubject.schoolYearTemplateSubjectId &&
      //        x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

      // if(isFalsy(schoolYearClassSubjectExamSchedule)) {

      //   enqueueSnackbar( t('There is no exam for this subject at class level'), { variant: 'warning',
      //     anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
      //   reset({...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00',  registrationMarks: []});
      //   return;
      // } 




      // Now we want to have load mark only when watchSchoolYearClassSubjectId have changed...
      // const {maxMark, status, id} = schoolYearClassSubjectExamSchedule;
      // const registrationMarks = await getSchoolYearClassRegistrationMarks(watchSchoolYearPeriodExamId, watchSchoolYearClassSubjectId );

      // _setId(id);
      // reset({...data, maxMark, schoolYearClassSubjectExamScheduleStatus: status, registrationMarks});

    }

    _();


  }, [watchSchoolYearPeriodExamId]);


  // this is use for manage exclude / include RegistrationExcludedSubject
  const [registrations, setRegistrations] = useState<IRegistration[]>([]);

  const [isRegistrationMarkLoading, setIsRegistrationMarkLoading] = useState<boolean>(false);
  useEffect(() => {
    async function _() {

      const data = getValues();

      reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });

      if (data.schoolYearClassId <= 0) {
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      if (watchSchoolYearPeriodExamId <= 0 || watchSchoolYearClassSubjectId <= 0) {
        // enqueueSnackbar( `${t('You have to select exam and subject' )} !!!`, { variant: 'info',
        //   anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, autoHideDuration : 500 }); 
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);

      if (isFalsy(schoolYearClassSubject)) {
        enqueueSnackbar(`${t('This subject is not valid in this context')} !!!`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      const schoolYearClassSubjectExamSchedule = schoolYearClassSubjectExamSchedules.find(
        x => x.schoolYearTemplateSubjectId === schoolYearClassSubject.schoolYearTemplateSubjectId &&
          x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

      if (isFalsy(schoolYearClassSubjectExamSchedule)) {
        enqueueSnackbar(`${t('This subject have not been scheduled for this exam')} `, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        reset({ ...data, maxMark: 0, schoolYearClassSubjectExamScheduleStatus: '00', registrationMarks: [] });
        return;
      }

      try {
        setIsRegistrationMarkLoading(true);
        const { maxMark, status, id } = schoolYearClassSubjectExamSchedule;
        const registrationMarks = await getSchoolYearClassRegistrationMarks(watchSchoolYearPeriodExamId, watchSchoolYearClassSubjectId);

        _setId(id);
        reset({
          ...data, maxMark, schoolYearClassSubjectExamScheduleStatus: status, registrationMarks,
          checkMarkByCompetence: registrationMarks.some(x => x.registrationMarkCompetences.some(y => y.mark > 0))
        });

      } catch (error) {

      } finally {
        setIsRegistrationMarkLoading(false);
      }


    }

    _();

  }, [watchSchoolYearClassSubjectId]);



  const getCurrentSubjectTemplate = (): ISchoolYearTemplateSubject => {
    if (isFalsy(schoolYearClassTemplate)) return defaultSchoolYearTemplateSubject;

    const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) return defaultSchoolYearTemplateSubject;

    const schoolYearTemplateSubject = schoolYearClassTemplate!.schoolYearTemplateSubjects.find(x =>
      x.id === schoolYearClassSubject!.schoolYearTemplateSubjectId);

    return schoolYearTemplateSubject || defaultSchoolYearTemplateSubject;
  }

  const getCurrentExamCompetences = (): ISchoolYearTemplateSubjectExamScheduleCompetence[] => {
    if (isFalsy(schoolYearClassTemplate)) return [];

    const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) return [];

    const schoolYearTemplateSubject = schoolYearClassTemplate!.schoolYearTemplateSubjects.find(x =>
      x.id === schoolYearClassSubject!.schoolYearTemplateSubjectId);

    if (isFalsy(schoolYearTemplateSubject)) return [];

    var schoolYearTemplateSubjectExamSchedule = schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find(x =>
      x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

    return schoolYearTemplateSubjectExamSchedule?.schoolYearTemplateSubjectExamScheduleCompetences || [];
  }

  useEffect(() => {
    setCurrentFormNameAtom(t('Mark'));
    setCurrentBasicTextFilterProps(defaultBasicTextFilterProps);
  }, []);



  /********** This use effect call retrieve data wich will call refetch and _data will be updated. 
and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    // if(_id > 0)
    //   retrieveData('ApplicationQuery',_id, refetch);  
  }, [_id]);


  useEffect(() => {

    // if(_data && _data.id > 0) {
    //   reset(_data);
    // }
  }, [_data]);


  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    reset(defaultSchoolYearClassSubjectMark);
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!checkEntitySaveAuthorization('SchoolYearClassSubjectMark', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();

    const { autoDistributeMarkByCompetence, maxMark } = getValues();

    if (data.registrationMarks.length <= 0) {
      enqueueSnackbar(t('There is no mark to save'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const schoolYearTemplateSubjectExamScheduleIds =
      Array.from(new Set(data.registrationMarks.map(s => s.schoolYearTemplateSubjectExamScheduleId) || []));
    if (schoolYearTemplateSubjectExamScheduleIds.length !== 1) {
      enqueueSnackbar(t('All marks did not belong to the same exam'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.registrationMarks.some(x => x.schoolYearTemplateSubjectExamScheduleId <= 0)) {
      enqueueSnackbar(t('There is an error in the selection of exam'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }


    if (isFalsy(schoolYearClassTemplate)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Class template')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const schoolYearClassSubject = schoolYearClassSubjects.find(s => s.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Subject')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const schoolYearTemplateSubject = schoolYearClassTemplate.schoolYearTemplateSubjects.find(
      s => s.id === schoolYearClassSubject.schoolYearTemplateSubjectId);
    if (isFalsy(schoolYearTemplateSubject)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Subject')} +++`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const schoolYearTemplateSubjectExamSchedule = schoolYearTemplateSubject.schoolYearTemplateSubjectExamSchedules.find(
      e => e.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

    if (isFalsy(schoolYearTemplateSubjectExamSchedule)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Exam')} +++`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    // Set value of competences if we are in case of auto competence mark.
    if (autoDistributeMarkByCompetence) {

      data.registrationMarks = data.registrationMarks.map(r => {
        const sumMax = r.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark, 0);
        return {
          ...r,
          registrationMarkCompetences: r.registrationMarkCompetences.map(c => ({
            ...c,
            markExist: r.markExist ? c.markExist : false,
            mark: (r.markExist && c.markExist && sumMax !== 0) ?
              (autoDistributeMarkByCompetence ? r.mark * c.maxMark / sumMax : c.mark) : 0
          }))
        }
      }
      )
    }

    if (data.registrationMarks.some(r => r.mark !== 0 &&
      r.registrationMarkCompetences.length !== schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Competence')} ###`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.registrationMarks.some(r => r.mark !== 0 &&
      schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !== 0 &&
      r.registrationMarkCompetences.filter(c => c.markExist).length === 0)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Competence')} ### ${t('Exist ?')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.registrationMarks.some(r => !r.markExist && r.registrationMarkCompetences.some(c => c.markExist))) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Competence')} == ${t('Exist ?')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }


    if (schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.some(c => c.maxMark <= 0)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Competence')} - - ${t('Max mark')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.registrationMarks.some(c => maxMark < c.mark)) {
      enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Max')} / ${t('Mark')}`, {
        variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const badCompetenceRegistrationMarks = data.registrationMarks.filter(r =>
      r.registrationMarkCompetences.some(c => c.maxMark < c.mark));

    if (badCompetenceRegistrationMarks.length !== 0) {

      enqueueSnackbar(`${t('Invalid data !!!')} - ${t('Competence')} - ${t('Max')} / ${t('Mark')}`, {
        variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    let sumMaxMark = 0;
    if (schoolYearTemplateSubjectExamSchedule.schoolYearTemplateSubjectExamScheduleCompetences.length !== 0) {

      const badMarks = data.registrationMarks.filter(r => r.mark > 0 &&
        (sumMaxMark = r.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.maxMark, 0)) !== 0 &&
        Math.abs(r.mark -
          r.registrationMarkCompetences.filter(c => c.markExist).reduce((acc, curr) => acc + curr.mark, 0)) > 0.0001);

      if (badMarks.some(w => true)) {
        enqueueSnackbar(`${t('Invalid data !!!')}  - ${t('Mark')} / ${t('Competence')} `, {
          variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });

        setIsSaveLoading(false);
        return;
      }
    }


    // const sumMark = row.registrationMarkCompetences.reduce((acc, curr) => acc + curr.mark , 0);
    // const sumMaxMark = row.registrationMarkCompetences.reduce((acc, curr) => acc + curr.maxMark , 0);

    // const markCompetence = (sumMark*maxMark)/sumMaxMark;

    const schoolYearTemplateSubjectExamScheduleId = schoolYearTemplateSubjectExamScheduleIds[0];

    mutate({ ...data, schoolYearTemplateSubjectExamScheduleId });
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {

    if (_id <= 0 && watchSchoolYearClassId > 0) {
      const arr = await registrationServiceRef.current.getRegistrationsBySchoolYearClass(watchSchoolYearClassId);
      setRegistrations(arr);
      openEntityActionDrawer('SchoolYearClass', watchSchoolYearClassId);
    } else {
      setRegistrations(getValues().registrationMarks.map(x => ({ ...x, id: x.registrationId } as unknown as IRegistration)));
      openEntityActionDrawer('SchoolYearClassSubjectExamSchedule', _id);
    }

  }

  const [registrationSubjectExcludedSubjectId, setRegistrationSubjectExcludedSubjectId] = useState<number>(0);
  const [excludedRegistrationIdsForSubject, setExcludedRegistrationIdsForSubject] = useState<number[]>([]);
  const [openExcludeSubjectRegistrationsDialog, setOpenExcludeSubjectRegistrationsDialog] = useState<boolean>(false);
  const actionDataExcludeSubjectRegistrationsSchoolYearClassSubjectExamSchedule = async (event: MouseEvent<HTMLButtonElement>) => {

    setExcludedRegistrationIdsForSubject([]);
    setOpenExcludeSubjectRegistrationsDialog(true);
  }

  const actionDataExcludeSubjectRegistrationsSchoolYearClass = async (event: MouseEvent<HTMLButtonElement>) => {

    setExcludedRegistrationIdsForSubject([]);
    setOpenExcludeSubjectRegistrationsDialog(true);
  }


  const handleOkExcludeSubjectRegistrations = async () => {
    if (registrationSubjectExcludedSubjectId <= 0) {
      enqueueSnackbar(`${t('Invalid data !!!')} - ${t('Subject')} :::`, {
        variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOpenExcludeSubjectRegistrationsDialog(false);
    mutateExcludeSubjectRegistrations({
      id: _id,
      registrationIds: excludedRegistrationIdsForSubject,
      schoolYearClassSubjectId: registrationSubjectExcludedSubjectId
    });
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    const data = getValues();

    const arr = await getSchoolYearClassSubjectExamSchedules(watchSchoolYearClassId, watchSchoolYearPeriodId, watchSchoolYearPeriodExamId);

    setSchoolYearClassSubjectExamSchedules(arr);

    const schoolYearClassSubject = schoolYearClassSubjects.find(x => x.id === watchSchoolYearClassSubjectId);
    if (isFalsy(schoolYearClassSubject)) {
      enqueueSnackbar(`${t('This subject is not valid in this context')} !!!`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const schoolYearClassSubjectExamSchedule = arr.find(
      x => x.schoolYearTemplateSubjectId === schoolYearClassSubject.schoolYearTemplateSubjectId &&
        x.schoolYearPeriodExamId === watchSchoolYearPeriodExamId);

    if (isFalsy(schoolYearClassSubjectExamSchedule)) {
      enqueueSnackbar(`${t('This subject have not been scheduled for this exam')} `, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const { maxMark, status, id } = schoolYearClassSubjectExamSchedule;

    refetchSchoolYearClassTemplate();

    _setId(id);
    reset({ ...data, maxMark, schoolYearClassSubjectExamScheduleStatus: status });
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={1.5} columnSpacing={1.5}>
          <Grid item xs={12} md={4} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t('Class')} ...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >

                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnActionExcludeSubjectRegistrationsSchoolYearClassSubjectExamSchedule' onClick={actionDataExcludeSubjectRegistrationsSchoolYearClassSubjectExamSchedule} sx={{ display: 'none' }} />
                <Button id='btnActionExcludeSubjectRegistrationsSchoolYearClass' onClick={actionDataExcludeSubjectRegistrationsSchoolYearClass} sx={{ display: 'none' }} />


                <TextField sx={{ width: 'calc(50% - 8px)' }} id="schoolYearClassName"
                  label={t('Class')} {...register('schoolYearClassName')} inputProps={{ readOnly: true }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={handleClickOpenSchoolYearClass}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  helperText={(getValues().schoolYearClassId) > 0 ?
                    <FormHelperText>
                      <Stack direction="row" spacing={2}>

                        <Link
                          href="#"
                          onClick={handleClassNavigationClick}
                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                        >
                          {t('Details')}
                        </Link>

                        <Link
                          href="#"
                          onClick={handleFilterSameLevelClasses}
                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'green' }}
                        >
                          {t('Show same level')}
                        </Link>

                      </Stack>
                    </FormHelperText> : null
                  } />
                <Controller name='studyType' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyType"
                      label={t('Study type')} inputProps={{ readOnly: false }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_TYPE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

                {openClassFilter && <FormDialog open={openClassFilter} maxWidth='md'
                  okText={''} cancelText={t('Cancel')} title={t('Classes')} onCancel={() => { setOpenClassFilter(false); }}
                  onClose={() => { setOpenClassFilter(false); }} onOk={() => { }}  >
                  <BasicTextFilterForm<ISchoolYearClass> {...basicFilterSchoolYearClass} />
                </FormDialog>}

                {openSchoolYearClassByTeacher && <FormDialog open={openSchoolYearClassByTeacher} maxWidth='md'
                  okText={''} cancelText={''} title={t('Classes')}
                  onCancel={() => { setOpenSchoolYearClassByTeacher(false); }}
                  onClose={() => { setOpenSchoolYearClassByTeacher(false); }} onOk={() => { }}  >
                  <Stack flexDirection='column'>
                    <Box sx={{ mt: 1, width: '100%', display: 'flex', alignItems: 'center' }}>
                      <TextField select onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                        const idSchoolYear = Number(event.target.value)
                        setSelectedSchoolYearByTeacher(idSchoolYear);
                        setSchoolYearClassesByTeacher(await getSchoolYearClassesByTeacher(idSchoolYear, teacherId));
                      }} value={selectedSchoolYearByTeacher} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                        label={t('School year')} inputProps={{ readOnly: false }} focused >
                        {schoolYears && schoolYears.map(
                          (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                        }
                      </TextField>
                      <Button
                        variant="contained"
                        onClick={() => { setOpenClassFilter(true); setOpenSchoolYearClassByTeacher(false); }}
                        sx={{ ml: 2 }}
                      >
                        {t('Switch to global filter')}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 3, width: '100%' }} >
                      <EnhancedTable<ISchoolYearClass>
                        rows={schoolYearClassesByTeacher}
                        headCells={[
                          { id: 'id', label: t('Id'), display: true, type: 'numeric', width: 5 },
                          { id: 'classTemplateName', label: t('Class template'), display: true, type: 'string', width: 30 },

                          { id: 'studyLevel', label: t('Study level'), display: true, type: 'string', width: 15 },
                          { id: 'studyLanguage', label: t('Study language'), display: true, type: 'string', width: 15 },

                          { id: 'name', label: t('Name'), display: true, type: 'string', width: 35 },
                        ]}
                        title={t(`Subject`)} objKey='id'
                        stateSelected={undefined}
                        onRowSelected={undefined} rowCheckedMode='single'
                        onRowCheckedSelectChange={undefined} order='desc' orderBy='name'
                        onRowDoubleClick={handleDoubleClickSchoolYearClassByTeacher}
                        rowActionIcon={undefined}
                        toolbarActions={[
                          // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                        ]}
                      />
                    </Box>
                  </Stack>
                </FormDialog>}

                {openSchoolYearClassByLevel && <FormDialog open={openSchoolYearClassByLevel} maxWidth='md'
                  okText={''} cancelText={''} title={t('Classes')}
                  onCancel={() => { setOpenSchoolYearClassByLevel(false); }}
                  onClose={() => { setOpenSchoolYearClassByLevel(false); }} onOk={() => { }}  >
                  <Stack flexDirection='column'>
                    
                    <Box sx={{ mt: 3, width: '100%' }} >
                      <EnhancedTable<ISchoolYearClass>
                        rows={schoolYearClassesByLevel}
                        headCells={[
                          { id: 'id', label: t('Id'), display: true, type: 'numeric', width: 5 },
                          { id: 'classTemplateName', label: t('Class template'), display: true, type: 'string', width: 30 },

                          { id: 'studyLevel', label: t('Study level'), display: true, type: 'string', width: 15 },
                          { id: 'studyLanguage', label: t('Study language'), display: true, type: 'string', width: 15 },

                          { id: 'name', label: t('Name'), display: true, type: 'string', width: 35 },
                        ]}
                        title={t(`Subject`)} objKey='id'
                        stateSelected={undefined}
                        onRowSelected={undefined} rowCheckedMode='single'
                        onRowCheckedSelectChange={undefined} order='desc' orderBy='name'
                        onRowDoubleClick={handleDoubleClickSchoolYearClassByLevel}
                        rowActionIcon={undefined}
                        toolbarActions={[
                          // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                        ]}
                      />
                    </Box>
                  </Stack>
                </FormDialog>}


              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="classTemplateName"
                  label={t('Class template')} {...register('classTemplateName')} inputProps={{ readOnly: true }} />
                <Controller name='studyLanguage' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyLanguage"
                      label={t('Study language')} inputProps={{ readOnly: true }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="fullTeacherName"
                  label={t('Full teacher')} {...register('fullTeacherName')} inputProps={{ readOnly: true }} />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="substituteTeacherName"
                  label={t('Substitute teacher')} {...register('substituteTeacherName')} inputProps={{ readOnly: true }} />
              </Box>
              <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t('Exam')} ...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='schoolYearId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('School year')} inputProps={{ readOnly: false }} focused >
                      {schoolYears && schoolYears.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller control={control}
                  name='schoolYearStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Start date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />

                <Controller control={control}
                  name='schoolYearEndDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('End date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='schoolYearPeriodId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('Period')} inputProps={{ readOnly: false }} focused >
                      {schoolYearPeriods && schoolYearPeriods.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='schoolYearPeriodExamId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('Exam')} inputProps={{ readOnly: false }} focused >
                      {schoolYearPeriodExams && schoolYearPeriodExams.filter(x => x.schoolYearPeriodId === watchSchoolYearPeriodId).map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t('Subject')} ...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }}
                key={`${(schoolYearClassSubjects || []).map(s => s.schoolYearTemplateSubjectLongName).join('-')}`} >
                <Controller name='schoolYearClassSubjectId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }}
                      id="schoolYearClassSubjectId"
                      helperText={(watchSchoolYearClassId > 0 && watchSchoolYearPeriodExamId > 0) ?
                        <FormHelperText>
                          <Link
                            href="#"
                            onClick={handleOpenSchoolYearClassSubjectExamSchedule}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                          >
                            {t('Status of marks per subject')}
                          </Link>
                        </FormHelperText> : null
                      }
                      label={t('Subject')} inputProps={{ readOnly: false }} inputRef={inputLogin} focused >
                      <MenuItem key={0} value={0}>::::</MenuItem>
                      {schoolYearClassSubjects && schoolYearClassSubjects.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.schoolYearTemplateSubjectLongName}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="teacherName"
                  label={t('Teacher')} {...register('teacherName')} inputProps={{ readOnly: true }} />
                {openSchoolYearClassSubjectExamSchedule && <FormDialog open={openSchoolYearClassSubjectExamSchedule} maxWidth='sm'
                  okText={''} cancelText={''} title={t('Status of marks per subject')}
                  onCancel={() => { setOpenSchoolYearClassSubjectExamSchedule(false); }}
                  onClose={() => { setOpenSchoolYearClassSubjectExamSchedule(false); }} onOk={() => { }}  >
                  <Stack flexDirection='column'>
                    <Box sx={{ mt: 4, width: '100%' }} >
                      <EnhancedTable<ISchoolYearClassSubjectExamSchedule>
                        rows={schoolYearClassSubjectExamSchedules}
                        headCells={[
                          //{id:'id', label : t('Id'),  display: true, type: 'numeric', },

                          { id: 'subjectLongName', label: t('Subject'), display: true, type: 'string', width: 55 },

                          { id: 'status', label: t('Status'), display: true, type: 'status', width: 45, /*getOptions: getSubjectExamStatusList */ },
                        ]}
                        title={t(`Subject`)} objKey='id'
                        stateSelected={undefined}
                        onRowSelected={undefined} rowCheckedMode='single'
                        onRowCheckedSelectChange={undefined} order='desc' orderBy='subjectLongName'
                        onRowDoubleClick={undefined}
                        rowActionIcon={undefined}
                        toolbarActions={[
                          // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                        ]}
                      />
                    </Box>
                  </Stack>
                </FormDialog>}
              </Box>
              <Box sx={{ mt: 4, width: '100%' }} >
                {/* <Controller 
                                  name={`methodName`}
                                  control={control}
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(100% - 8px)'}} id="methodName"
                                      label={t('Data preparation method')} inputProps={ {readOnly: false}}>                                
                                      {currentLibraryClass.externalLibraryClassMethods.map( 
                                          (x,idx) => <MenuItem key={x.methodName} value={x.methodName}>{x.methodName}</MenuItem> )
                                        }
                                    </TextField>
                                  )}
                              /> */}
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={8} component={Paper} >
            <Stack flexDirection='column'>
              {(getValues().registrationMarks.some(m => m.registrationMarkCompetences.length > 0)) &&
                <Box sx={{ mt: 1, width: '100%' }} >
                  <FormControlLabel sx={{ width: 'calc(35% - 8px)' }}
                    label={`${t('Check marks by competence ?')}`}
                    control={
                      <Controller
                        name='checkMarkByCompetence'
                        control={control}
                        render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                      />} />
                  {watchCheckMarkByCompetence && <FormControlLabel sx={{ width: 'calc(50% - 8px)' }}
                    label={`${t('Ditribute marks by competence auto. ?')}`}
                    control={
                      <Controller
                        name='autoDistributeMarkByCompetence'
                        control={control}
                        render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                      />} />}
                </Box>}
              {(getValues().registrationMarks.some(m => m.registrationMarkCompetences.length > 0)) &&
                <Box sx={{ mt: 2.25, width: '100%', cursor: 'pointer' }}
                  key={`${getCurrentSubjectTemplate().schoolYearTemplateSubjectCompetences.map((x, idx) =>
                    `${x.id}-${getCurrentExamCompetences().some(c => c.schoolYearTemplateSubjectCompetenceId === x.id)}`)}`}
                >
                  {getCurrentSubjectTemplate().schoolYearTemplateSubjectCompetences.map((x, idx) =>
                    <Chip
                      sx={{
                        ml: 1, mr: 1, mb: 1,
                        border: getCurrentExamCompetences().some(c => c.schoolYearTemplateSubjectCompetenceId === x.id) ?
                          '2px solid green' : 'none',
                        whiteSpace: 'normal',    // ← allows wrapping
                        height: 'auto',          // ← allows multi-line height
                        paddingY: 1,             // optional for better spacing
                      }}
                      key={`recipient-${x.id}-${x.title}-${idx}`}
                      label={`${x.description}`}
                      color={getCurrentExamCompetences().some(c => c.schoolYearTemplateSubjectCompetenceId === x.id) ?
                        'primary' : 'secondary'} variant="filled"
                      onClick={(event) => { openEntityActionDrawer('SchoolYearTemplateSubjectCompetence', x.id); }}
                    //onDelete={() => {alert('');}}
                    />)
                  }
                </Box>}
              {isRegistrationMarkLoading ?
                <Box sx={{
                  mt: 1,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <GridSpinner
                    color='#00BFFF'
                    height={'100%'}
                  //width={100}
                  //timeout={3000} //3 secs
                  />
                </Box> :
                <Box sx={{ mt: 1, width: '100%' }}
                  key={` ${watchSchoolYearPeriodExamId} ${getValues().maxMark} ${watchSchoolYearClassSubjectId} - 
                                    ${getValues().registrationMarks.map(a => String(a.mark)).join('-')} --
                                    ${getValues().registrationMarks.map(a => String(a.markExist)).join('-')}
                                    ${getValues().registrationMarks.map(a => a.registrationMarkCompetences.map(b => String(b.mark)).join(':')).join('-')}  `} >





                  <ArrayFieldTableEx<ISchoolYearClassSubjectMark, IRegistrationMark, '_id'>
                    mainObject={getValues()} fieldKey='id'
                    headCells={headRegistrationMarkCells} rowsPathName={`registrationMarks`}
                    title={`${t('Student mark out of')} ${getValues().maxMark} ${t('in')} ${getValues().schoolYearTemplateSubjectLongName}`}
                    rowActionIcon={registrationMarkRowActionIcon}
                    //onRowSelected={handleQuerySelected}

                    refAppend={refAppendRegistrationMarks as MutableRefObject<(value: Partial<FieldArray<ISchoolYearClassSubjectMark>> | Partial<FieldArray<ISchoolYearClassSubjectMark>>[], options?: FieldArrayMethodProps) => void>}
                    refUpdate={refUpdateRegistrationMark as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYearClassSubjectMark>>) => void>}
                    refRemove={refRemoveRegistrationMark as MutableRefObject<(index: number) => void>}

                    rowCheckedMode='multiple'
                    //onRowCheckedSelectChange={handleRowCheckedSelectChange}
                    stateSelectedIndex={[selectedRegistrationsIndex, setSelectedRegistrationsIndex]}
                    //displayMore={undefined}
                    toolbarActions={[
                      {
                        toolTip: `${t('Set same mark for selected students')}...`,
                        onClickIcon: handleSetMarkForSelected, icon: BorderColor,
                      },
                      {
                        toolTip: `${t('Set same mark for all students')}...`, onClickIcon: handleSetMarkForAll,
                        icon: DoneAll,
                      },

                      {
                        toolTip: `${t('Exclude/Include mark for all students')}...`,
                        onClickIcon: handleExcludeOrIncludeMarkForAll, icon: ToggleOn,
                      },

                      {
                        toolTip: `${t('Set same mark of competence for all students')}...`,
                        onClickIcon: handleSetCompetenceMarkForAll, icon: WorkspacePremium,
                      },

                      {
                        toolTip: `${t('Exclude/Include mark of competence for all students')}...`,
                        onClickIcon: handleExcludeOrIncludeCompetenceMarkForAll, icon: TaskAlt,
                      },

                      {
                        toolTip: `${t('Download marks as Excel file and prepare for upload')}...`,
                        onClickIcon: handleDownloadMarks, icon: FileDownloadIcon,
                      },

                      { toolTip: `${t('Upload marks from file')}...`, onClickIcon: handleLoadMarks, icon: FileUploadIcon, },


                    ]}
                  />
                  {openMarkPurpose && (markIndex >= 0) && <FormDialog open={openMarkPurpose} maxWidth='md' height='80vh'
                    okText={t('Close')} cancelText={''} title={`${t('Mark purpose')} / ${t('Marks per competence')}`} onCancel={() => { setOpenMarkPurpose(false); }}
                    onClose={() => { setOpenMarkPurpose(false); }} onOk={() => { setOpenMarkPurpose(false); }}  >
                    <Stack flexDirection='column'>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... `} >
                        <FormControlLabel sx={{ width: 'calc(40% - 8px)' }}
                          label={t('Exist ?')}
                          control={
                            <Controller
                              name={`registrationMarks.${markIndex}.markExist`}
                              control={control}
                              render={({ field: { value, onChange, ...props } }) =>
                                <Checkbox {...props} checked={value} onChange={onChange}
                                  disabled={(getValues().registrationMarks[markIndex].isSubjectExcluded &&
                                    !getValues().registrationMarks[markIndex].markExist) ||
                                    !['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)} />}
                            />} />
                        {!getValues().registrationMarks[markIndex].markExist &&
                          <Controller name={`registrationMarks.${markIndex}.markPurpose`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <TextField select onChange={onChange} value={value} sx={{ width: 'calc(60% - 8px)' }} id="markPurpose"
                                label={t('Purpose')} inputProps={{
                                  readOnly: (getValues().registrationMarks[markIndex].isSubjectExcluded &&
                                    !getValues().registrationMarks[markIndex].markExist) ||
                                    !['00', '95'].includes(getValues().schoolYearClassSubjectExamScheduleStatus)
                                }} focused >
                                {enumItems && enumItems.filter(e =>
                                  e.enumerationCode === Enum_REGISTRATION_MARK_PURPOSE).map(
                                    (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                                }
                              </TextField>
                            )}
                          />}
                      </Box>
                      <Box sx={{ mt: 2, width: '100%' }}
                        key={` ${watchSchoolYearPeriodExamId} ${getValues().maxMark} ${watchSchoolYearClassSubjectId} --
                                                ${getValues().registrationMarks[markIndex].registrationMarkCompetences.map(a => String(a.markExist)).join('-')}
                                                ${getValues().registrationMarks[markIndex].registrationMarkCompetences.map(a => String(a.mark)).join('-')}
                                          `}
                      >
                        <ArrayFieldTableEx<ISchoolYearClassSubjectMark, IRegistrationMarkCompetence, '_id'>
                          mainObject={getValues()} fieldKey='id'
                          headCells={headRegistrationMarkCompetenceCells} rowsPathName={`registrationMarks.${markIndex}.registrationMarkCompetences`}
                          title={`${t('Marks per competence')} ${getValues().maxMark} ${t('in')} ${getValues().schoolYearTemplateSubjectLongName}`}
                          //rowActionIcon={registrationMarkRowActionIcon}  
                          //onRowSelected={handleQuerySelected}

                          refAppend={refAppendRegistrationMarkCompetences as MutableRefObject<(value: Partial<FieldArray<ISchoolYearClassSubjectMark>> | Partial<FieldArray<ISchoolYearClassSubjectMark>>[], options?: FieldArrayMethodProps) => void>}
                          refUpdate={refUpdateRegistrationMarkCompetence as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYearClassSubjectMark>>) => void>}
                          refRemove={refRemoveRegistrationMarkCompetence as MutableRefObject<(index: number) => void>}

                          rowCheckedMode='multiple'
                        //onRowCheckedSelectChange={handleRowCheckedSelectChange}
                        //stateSelectedIndex={[selectedRegistrationsIndex, setSelectedRegistrationsIndex]}
                        //displayMore={undefined}
                        // toolbarActions={[
                        // { toolTip: `${t('Set same mark for selected students')}...`, onClickIcon: handleSetMarkForSelected ,icon: CheckBoxIcon,  },
                        // { toolTip: `${t('Set same mark for all students')}...`, onClickIcon: handleSetMarkForAll ,icon: SelectAllIcon,  },
                        // ]}
                        />
                      </Box>
                    </Stack>
                  </FormDialog>}

                  {openSameMarkDialog && <FormDialog open={openSameMarkDialog} maxWidth='xs' height='35vh'
                    okText={t('OK')} cancelText={t('Cancel')} title={t('Set same mark')} onCancel={() => { setOpenSameMarkDialog(false); }}
                    onClose={() => { setOpenSameMarkDialog(false); }} onOk={handleSetMarkOk}  >
                    <Stack flexDirection='column'>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... mark`} >

                        <NumberFormat
                          label={t('Mark')} sx={{ width: 'calc(100% - 8px)' }}
                          allowEmptyFormatting={false}
                          control={control}
                          thousandSeparator={true}
                          decimalScale={2}
                          onValueChange={(v) => setSameMark(v.floatValue || 0)}
                          defaultValue={sameMark}
                          value={sameMark}
                          customInput={TextFieldRight}
                        />
                      </Box>
                    </Stack>
                  </FormDialog>}

                  {openExcludeIncludeDialog && <FormDialog open={openExcludeIncludeDialog} maxWidth='xs' height='45vh'
                    okText={t('OK')} cancelText={t('Cancel')} title={`${t('Exclude')} / ${t('Include')}`} onCancel={() => { setOpenExcludeIncludeDialog(false); }}
                    onClose={() => { setOpenExcludeIncludeDialog(false); }} onOk={handleExcludeOrIncludeMarkForAllOk}  >
                    <Stack flexDirection='column'>
                      <Box sx={{ mt: 1, width: '100%' }} >
                        <FormControlLabel sx={{ width: 'calc(100% - 8px)' }}
                          label={t('Exist ?')}
                          control={
                            <Checkbox checked={sameExcludeInclude} onChange={(e) => setSameExcludeInclude(e.target.checked)} />} />
                      </Box>
                      {!sameExcludeInclude && <Box sx={{ mt: 1, width: '100%' }} key={` ... purpose `} >
                        <TextField select onChange={(e) => setSameExcludeIncludePurpose(e.target.value)} value={sameExcludeIncludePurpose}
                          sx={{ width: 'calc(100% - 8px)' }} id="markPurpose"
                          label={t('Purpose')} inputProps={{ readOnly: false }} focused >
                          {enumItems && enumItems.filter(e =>
                            e.enumerationCode === Enum_REGISTRATION_MARK_PURPOSE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      </Box>}
                    </Stack>
                  </FormDialog>}

                  {openSameCompetenceMarkDialog && <FormDialog open={openSameCompetenceMarkDialog} maxWidth='sm' height='35vh'
                    okText={t('OK')} cancelText={t('Cancel')} title={`${t('Set same mark')} - ${t('Competence')}`}
                    onCancel={() => { setOpenSameCompetenceMarkDialog(false); }}
                    onClose={() => { setOpenSameCompetenceMarkDialog(false); }} onOk={handleSetCompetenceMarkForAllOk}  >
                    <Stack flexDirection='column'>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... competence`} >
                        <TextField select onChange={(e) => setSelectedCompetenceId(Number(e.target.value))} value={selectedCompetenceId}
                          sx={{ width: 'calc(100% - 8px)' }} id="markPurpose"
                          label={t('Competence')} inputProps={{ readOnly: false }} focused >
                          {getCurrentExamCompetences().map(
                            (x, idx) => <MenuItem key={x.schoolYearTemplateSubjectCompetenceId}
                              value={x.schoolYearTemplateSubjectCompetenceId}>{x.schoolYearTemplateSubjectCompetenceDescription}</MenuItem>)
                          }
                        </TextField>
                      </Box>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... mark`} >
                        <NumberFormat
                          label={t('Mark')} sx={{ width: 'calc(100% - 8px)' }}
                          allowEmptyFormatting={false}
                          control={control}
                          thousandSeparator={true}
                          decimalScale={2}
                          onValueChange={(v) => setSameCompetenceMark(v.floatValue || 0)}
                          defaultValue={sameCompetenceMark}
                          value={sameCompetenceMark}
                          customInput={TextFieldRight}
                        />
                      </Box>
                    </Stack>
                  </FormDialog>}

                  {openExcludeIncludeCompetenceDialog && <FormDialog open={openExcludeIncludeCompetenceDialog} maxWidth='sm' height='45vh'
                    okText={t('OK')} cancelText={t('Cancel')} title={`${t('Exclude')} / ${t('Include')} - ${t('Competence')}`}
                    onCancel={() => { setOpenExcludeIncludeCompetenceDialog(false); }}
                    onClose={() => { setOpenExcludeIncludeCompetenceDialog(false); }} onOk={handleExcludeOrIncludeCompetenceMarkForAllOk}  >
                    <Stack flexDirection='column'>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... competence`} >
                        <TextField select onChange={(e) => setSelectedCompetenceId(Number(e.target.value))} value={selectedCompetenceId}
                          sx={{ width: 'calc(100% - 8px)' }} id="competence - exclude - include"
                          label={t('Competence')} inputProps={{ readOnly: false }} focused >
                          {getCurrentExamCompetences().map(
                            (x, idx) => <MenuItem key={x.schoolYearTemplateSubjectCompetenceId}
                              value={x.schoolYearTemplateSubjectCompetenceId}>{x.schoolYearTemplateSubjectCompetenceDescription}</MenuItem>)
                          }
                        </TextField>
                      </Box>
                      <Box sx={{ mt: 1, width: '100%' }} key={` ... exists`} >

                        <FormControlLabel sx={{ width: 'calc(100% - 8px)' }}
                          label={t('Exist ?')}
                          control={
                            <Checkbox checked={sameExcludeIncludeCompetence}
                              onChange={(e) => setSameExcludeIncludeCompetence(e.target.checked)} />} />
                      </Box>
                      {!sameExcludeIncludeCompetence && <Box sx={{ mt: 1, width: '100%' }} key={` ... purpose`} >
                        <TextField select onChange={(e) => setSameExcludeIncludePurpose(e.target.value)} value={sameExcludeIncludePurpose}
                          sx={{ width: 'calc(100% - 8px)' }} id="markPurpose"
                          label={t('Purpose')} inputProps={{ readOnly: false }} focused >
                          {enumItems && enumItems.filter(e =>
                            e.enumerationCode === Enum_REGISTRATION_MARK_PURPOSE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      </Box>}


                    </Stack>
                  </FormDialog>}

                  {openBasicImport2Form && <FormDialog open={openBasicImport2Form} maxWidth='sm' height='45vh'
                    okText={t('Import')} cancelText={t('Cancel')} title={`${t('Import')} ....`} onCancel={() => { setOpenBasicImport2Form(false); }}
                    onClose={() => { setOpenBasicImport2Form(false); }} onOk={handleOKBasicImport2Marks}  >
                    <BasicImport2Form<IRegistrationMark, IRegistrationMarkCompetence>
                      fileNameToLoad={`${t('Marks')}-${getValues().schoolYearClassName}-${refSchoolYearPeriodExamName.current}-${getValues().schoolYearTemplateSubjectLongName}.xlsx`}
                      stateItems={[markObjecBasicImport2, setMarkObjecBasicImport2]}
                      columns={{
                        first: ['firstName', 'lastName', 'mark'],
                        second: ['firstName', 'lastName', 'schoolYearTemplateSubjectCompetenceTitle', 'mark'],
                      }}
                      defaultItem={{ first: defaultRegistrationMark, second: defaultRegistrationMarkCompetence }}

                      sheetName={{
                        first: `${t('Mark')}`,
                        second: `${t('Mark')}-${t('Competences')}`
                      }}
                    />
                  </FormDialog>}
                </Box>}

              {openExcludeSubjectRegistrationsDialog && <FormDialog open={openExcludeSubjectRegistrationsDialog} maxWidth='md' height='80vh'
                okText={t('OK')} cancelText={t('Cancel')} title={t('Attendance for subject - exclude / include students')}
                onCancel={() => { setOpenExcludeSubjectRegistrationsDialog(false); }}
                onClose={() => { setOpenExcludeSubjectRegistrationsDialog(false); }}
                onOk={handleOkExcludeSubjectRegistrations}  >
                <ExcludeSubjectRegistrationsForm
                  schoolYearClassSubjects={allSchoolYearClassSubjects.filter(x => !isTeacher || x.teacherId === teacherId)}
                  registrations={registrations}
                  excludedRegistrationIds={excludedRegistrationIdsForSubject}
                  onExludeRegistration={(id: number) => {
                    if (id <= 0)
                      setExcludedRegistrationIdsForSubject(registrations.map(x => x.id));
                    else
                      setExcludedRegistrationIdsForSubject(prev => [...prev, id]);
                  }}
                  onIncludeRegistration={(id: number) => {
                    if (id <= 0)
                      setExcludedRegistrationIdsForSubject([]);
                    else
                      setExcludedRegistrationIdsForSubject(prev => prev.filter(rid => rid !== id));
                  }}

                  onSubjectChange={async (schoolYearClassSubjectId: number) => {
                    setRegistrationSubjectExcludedSubjectId(schoolYearClassSubjectId);
                    const arr = await registrationServiceRef.current.getRegistrationExcludedSubjects(schoolYearClassSubjectId, 0);
                    setExcludedRegistrationIdsForSubject(arr.map(a => a.registrationId));
                  }}
                />
              </FormDialog>}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}
